import { initializeDB } from '../config/db';
import { JHA_STORE, STATUS_PROCESS } from '../config/constants';
import axiosInstance from '../axiosInstance';
import buildJhaPayload from '../components/jha/utils/jhaPayloadAdapter';
import { injectMissingDocRows } from '../components/jha/utils/documentTriggerEvaluator';
import notificationService from './notificationService';

class JhaSyncService {
    constructor() {
        this.syncing = false;
        this.syncingInitial = false;
        this.maxRetries = 3;
        this.retryDelay = 1000; // 1 second base delay
        this.autoSyncStarted = false;
        this.onlineHandler = null;
        this.intervalId = null;
        // Track JHAs currently being sent to prevent duplicates
        this.currentlySending = new Set();
    }

    /**
     * Dispatch custom event for UI notifications
     */
    dispatchSyncEvent(eventName, detail = {}) {
        window.dispatchEvent(new CustomEvent(eventName, { detail }));
    }

    /**
     * Delay helper for sequential processing
     */
    async delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Retry logic with exponential backoff
     */
    async retryWithBackoff(fn, retries = this.maxRetries) {
        for (let i = 0; i < retries; i++) {
            try {
                return await fn();
            } catch (error) {
                const isLastAttempt = i === retries - 1;
                if (isLastAttempt) {
                    throw error;
                }
                const backoffDelay = this.retryDelay * Math.pow(2, i);
                console.log(`Retry attempt ${i + 1}/${retries} after ${backoffDelay}ms`);
                await this.delay(backoffDelay);
            }
        }
    }

    /**
     * Sync all pending JHAs to the backend
     */
    async syncPendingJhas() {
        if (this.syncing) return;

        this.syncing = true;
        try {
            const db = await initializeDB();
            const tx = db.transaction(JHA_STORE, 'readonly');
            const store = tx.objectStore(JHA_STORE);
            const allJhas = await store.getAll();

            // Find JHAs that need syncing
            const pendingJhas = allJhas.filter(jha => 
                (jha.status === 'finish' || jha.status === 'completed') && 
                (!jha.syncStatus || jha.syncStatus === 'pending')
            );

            console.log(`Found ${pendingJhas.length} JHAs pending sync`);

            for (const jha of pendingJhas) {
                await this.syncSingleJha(jha);
            }

            // Dispatch event to update UI
            if (pendingJhas.length > 0) {
                window.dispatchEvent(new Event('jhaUpdated'));
            }

        } catch (error) {
            console.error('Error during JHA sync:', error);
        } finally {
            this.syncing = false;
        }
    }

    /**
     * Sync a single JHA to the backend
     */
    async syncSingleJha(jha) {
        try {
            // Skip if already synced
            if (jha.syncStatus === 'synced') {
                return true;
            }
            
            // Prepare data for backend
            const apiPayload = buildJhaPayload(jha);
            if (!apiPayload) {
                throw new Error('Unable to build JHA payload for syncing');
            }

            const response = await axiosInstance.post('/jha/save', apiPayload);

            if (response.data.success) {
                // Update local record with sync status
                const db = await initializeDB();
                const tx = db.transaction(JHA_STORE, 'readwrite');
                const store = tx.objectStore(JHA_STORE);

                const updated = {
                    ...jha,
                    backendId: response.data.id,
                    syncStatus: 'synced',
                    syncedAt: new Date().toISOString()
                };

                await store.put(updated);
                await tx.done;

                console.log(`Successfully synced JHA ${jha.id}`);
                return true;
            }
        } catch (error) {
            console.error(`Failed to sync JHA ${jha.id}:`, error);

            // 422 = backend rejected due to missing required documents.
            const status = error?.response?.status;
            const data = error?.response?.data || {};
            if (status === 422 && Array.isArray(data.missing) && data.missing.length > 0) {
                // ВАЖНО: повтор того же payload снова упрётся в 422. Если оставить
                // запись как finish+pending — syncPendingJhas будет дёргать её
                // КАЖДЫЙ цикл (бесконечный 422 + спам снекбара, синк никогда не
                // завершится). Поэтому откатываем JHA в process (выходит из фильтра
                // ретрая и становится редактируемым) — юзер добавит документ и
                // отправит заново. Зеркалит foreground-обработку в JhaStepFour.
                try {
                    const db = await initializeDB();
                    const tx = db.transaction(JHA_STORE, 'readwrite');
                    // Добавляем недостающие (по мнению бэкенда) документы в список,
                    // чтобы при открытии JHA юзеру было куда приложить файл/форму.
                    const { stepTwo: injectedStepTwo } = injectMissingDocRows(jha.stepTwo, data.missing);
                    // status=process выводит запись из фильтра ретрая (только
                    // finish/completed). syncStatus → null, чтобы в списке не висел
                    // ложный бейдж "Pending sync".
                    await tx.objectStore(JHA_STORE).put({
                        ...jha,
                        stepTwo: injectedStepTwo,
                        status: STATUS_PROCESS,
                        submittedAt: null,
                        syncStatus: null,
                        stepFive: { ...(jha.stepFive || {}), completedAt: null },
                    });
                    await tx.done;
                    window.dispatchEvent(new Event('jhaUpdated'));
                } catch (e) {
                    console.warn('Failed to roll back 422-rejected JHA:', e);
                }
                // Notify UI so the user understands WHY their JHA didn't sync.
                try {
                    window.dispatchEvent(new CustomEvent('jhaMissingDocuments', {
                        detail: {
                            jhaId: jha.id,
                            missing: data.missing,
                            message: data.message || 'Required documents are missing.',
                        }
                    }));
                } catch (_) { /* ignore */ }
            }
            return false;
        }
    }

    /**
     * Sync all pending initial reports to the backend
     */
    async syncPendingInitialReports() {
        if (this.syncingInitial) {
            console.log('Initial report sync already in progress - SKIPPING');
            return { total: 0, success: 0, failed: 0, errors: [] };
        }

        this.syncingInitial = true;
        console.log('=== Starting Initial Report Sync ===');
        const results = {
            total: 0,
            success: 0,
            failed: 0,
            errors: []
        };

        try {
            const db = await initializeDB();
            const tx = db.transaction(JHA_STORE, 'readonly');
            const store = tx.objectStore(JHA_STORE);
            const allJhas = await store.getAll();

            // Find JHAs with pending initial reports
            const pendingInitialReports = allJhas.filter(jha => {
                const initialSubmission = jha.stepThree?.initialSubmission;
                return initialSubmission?.status === 'pending';
            });

            results.total = pendingInitialReports.length;
            console.log(`Found ${results.total} initial reports pending sync`);

            if (results.total === 0) {
                return results;
            }

            // Dispatch start event
            this.dispatchSyncEvent('initialReportSyncStart', { total: results.total });

            // Show browser notification if tab is hidden
            notificationService.showSyncStart(results.total);

            // Process each initial report sequentially with delay
            for (let i = 0; i < pendingInitialReports.length; i++) {
                const jha = pendingInitialReports[i];

                try {
                    // Dispatch progress event
                    this.dispatchSyncEvent('initialReportSyncProgress', {
                        current: i + 1,
                        total: results.total,
                        jhaId: jha.id
                    });

                    const success = await this.syncSingleInitialReport(jha);

                    if (success) {
                        results.success++;
                        this.dispatchSyncEvent('initialReportSyncItemSuccess', {
                            jhaId: jha.id,
                            jobNumber: jha.stepOne?.jobNumber || jha.stepOne?.locationName
                        });
                    } else {
                        results.failed++;
                        results.errors.push({
                            jhaId: jha.id,
                            error: 'Unknown error'
                        });
                    }

                    // Add delay between requests to avoid spamming (500ms)
                    if (i < pendingInitialReports.length - 1) {
                        await this.delay(500);
                    }

                } catch (error) {
                    console.error(`Failed to sync initial report for JHA ${jha.id}:`, error);
                    results.failed++;
                    results.errors.push({
                        jhaId: jha.id,
                        error: error.message
                    });
                }
            }

            // Dispatch completion event
            this.dispatchSyncEvent('initialReportSyncComplete', results);

            // Show browser notification (even if tab is hidden)
            notificationService.showSyncComplete(results.success, results.failed, results.total);

            // Update UI
            window.dispatchEvent(new Event('jhaUpdated'));

        } catch (error) {
            console.error('Error during initial report sync:', error);
            this.dispatchSyncEvent('initialReportSyncError', { error: error.message });
        } finally {
            this.syncingInitial = false;
            console.log('=== Finished Initial Report Sync ===');
        }

        return results;
    }

    /**
     * Sync a single initial report to the backend
     */
    async syncSingleInitialReport(jha) {
        const jhaId = jha.id;

        // Check if this JHA is already being sent
        if (this.currentlySending.has(jhaId)) {
            console.log(`JHA ${jhaId} is already being sent - SKIPPING duplicate`);
            return false;
        }

        try {
            const initialSubmission = jha.stepThree?.initialSubmission;

            // Skip if not pending
            if (initialSubmission?.status !== 'pending') {
                console.log(`JHA ${jhaId} status is ${initialSubmission?.status} - SKIPPING`);
                return true;
            }

            // Mark as currently sending
            this.currentlySending.add(jhaId);
            console.log(`Starting sync for JHA ${jhaId}`);

            // Prepare initial report data (only steps 1-3)
            const initialReportData = {
                ...jha,
                stepFour: undefined,
                stepFive: undefined,
                submittedAt: initialSubmission.pendingSince || new Date().toISOString()
            };

            const payload = buildJhaPayload(initialReportData);
            if (!payload) {
                throw new Error('Unable to build initial report payload');
            }

            payload.submittedAt = initialReportData.submittedAt;

            // Use retry logic for the request
            const response = await this.retryWithBackoff(async () => {
                return await axiosInstance.post('/jha/initial-report', payload);
            });

            const submittedAt = response?.data?.submittedAt || initialReportData.submittedAt;
            const reportId = response?.data?.id ?? null;

            // Update local record
            const db = await initializeDB();
            const tx = db.transaction(JHA_STORE, 'readwrite');
            const store = tx.objectStore(JHA_STORE);

            const updated = {
                ...jha,
                stepThree: {
                    ...jha.stepThree,
                    initialSubmission: {
                        status: 'sent',
                        submittedAt,
                        reportId,
                        pendingSince: undefined
                    }
                }
            };

            await store.put(updated);
            await tx.done;

            console.log(`✓ Successfully synced initial report for JHA ${jhaId}`);
            return true;

        } catch (error) {
            console.error(`✗ Failed to sync initial report for JHA ${jhaId}:`, error);

            const status = error?.response?.status;
            const data = error?.response?.data || {};
            if (status === 422 && Array.isArray(data.missing) && data.missing.length > 0) {
                // Повтор не пройдёт (те же недостающие документы). Снимаем
                // initialSubmission с 'pending' → 'rejected', иначе
                // syncPendingInitialReports берёт его каждый цикл (бесконечный 422).
                // JHA остаётся в process и редактируем: юзер добавит документ и
                // отправит initial заново (кнопка Send снова станет доступна).
                try {
                    const db = await initializeDB();
                    const tx = db.transaction(JHA_STORE, 'readwrite');
                    const store = tx.objectStore(JHA_STORE);
                    const fresh = await store.get(jhaId);
                    const base = fresh || jha;
                    // Добавляем недостающие (по мнению бэкенда) документы в список.
                    const { stepTwo: injectedStepTwo } = injectMissingDocRows(base.stepTwo, data.missing);
                    await store.put({
                        ...base,
                        stepTwo: injectedStepTwo,
                        stepThree: {
                            ...base.stepThree,
                            initialSubmission: {
                                ...(base.stepThree?.initialSubmission || {}),
                                status: 'rejected',
                                pendingSince: undefined,
                            },
                        },
                    });
                    await tx.done;
                    window.dispatchEvent(new Event('jhaUpdated'));
                } catch (e) {
                    console.warn('Failed to mark initial report 422-rejected:', e);
                }
                try {
                    window.dispatchEvent(new CustomEvent('jhaMissingDocuments', {
                        detail: {
                            jhaId,
                            kind: 'initial-report',
                            missing: data.missing,
                            message: data.message || 'Required documents are missing for the initial report.',
                        }
                    }));
                } catch (_) { /* ignore */ }
            }
            return false;
        } finally {
            // Always remove from currently sending set
            this.currentlySending.delete(jhaId);
            console.log(`Removed JHA ${jhaId} from sending queue`);
        }
    }

    /**
     * Check if online and try to sync both initial reports and full JHAs
     */
    async checkAndSync() {
        if (navigator.onLine) {
            // Sync initial reports first
            await this.syncPendingInitialReports();
            // Then sync full JHAs
            await this.syncPendingJhas();
        }
    }

    /**
     * Start automatic sync
     */
    startAutoSync(intervalMinutes = 5) {
        // Prevent multiple startups
        if (this.autoSyncStarted) {
            console.log('Auto sync already started');
            return;
        }

        this.autoSyncStarted = true;
        console.log('Starting JHA auto sync...');

        // Initial sync
        this.checkAndSync();

        // Set up interval (save ID for cleanup)
        this.intervalId = setInterval(() => {
            this.checkAndSync();
        }, intervalMinutes * 60 * 1000);

        // Create bound handler (so we can remove it later)
        this.onlineHandler = () => {
            console.log('Back online - syncing JHAs...');
            this.checkAndSync();
        };

        // Listen for online event
        window.addEventListener('online', this.onlineHandler);
    }

    /**
     * Stop automatic sync (cleanup)
     */
    stopAutoSync() {
        if (!this.autoSyncStarted) {
            return;
        }

        console.log('Stopping JHA auto sync...');

        // Clear interval
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        // Remove online listener
        if (this.onlineHandler) {
            window.removeEventListener('online', this.onlineHandler);
            this.onlineHandler = null;
        }

        this.autoSyncStarted = false;
    }
}

export default new JhaSyncService();
